import { createAsyncThunk } from '@reduxjs/toolkit'
import { useGraphCacheStore } from '@tunasong/graph-cache'
import { canWrite } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { entitiesApi } from '../../api/entities.js'
import type { RootState } from '../../configure-store.js'
import type { TunaThunkDispatch } from '../../hooks/thunk-dispatch.hook.js'
import invariant from 'tiny-invariant'

export const addChild = createAsyncThunk<
  Persisted<Entity>,
  { parentId: string; childId: string },
  { state: RootState; dispatch: TunaThunkDispatch }
>(
  'entities/addChild',
  async ({ parentId: providedParentId, childId }, { dispatch, getState }): Promise<Persisted<Entity>> => {
    const { userId } = getState().user

    invariant(userId, 'User must be logged in to add a child entity')

    const parentId = providedParentId === 'ROOT' ? null : providedParentId

    /**
     * A list loads its rows through the children query, so a folder never opened on its own is not in the loadEntity
     * cache: dropping onto it threw "not found" and the move was refused as if the user could not write it (GH #3060)
     */
    const load = async (id: string) =>
      entitiesApi.endpoints.loadEntity.select({ id })(getState())?.data ??
      dispatch(entitiesApi.endpoints.loadEntity.initiate({ id }, { subscribe: false })).unwrap()
    const parent = parentId ? await load(parentId) : null
    const child = await load(childId)
    const childETag = getState().entities.eTags[childId]

    if (!child || (parentId && !parent)) {
      throw new Error(`Entity ${childId} not found - this is unexpected`)
    }

    const canWriteChild = userId === child.userId || canWrite(userId, child.acls)
    const canWriteParent = !parent || userId === parent.userId || canWrite(userId, parent.acls ?? [])

    if (!(canWriteChild && canWriteParent)) {
      throw new Error(`User ${userId} does not have permission add child ${childId} to parent ${parentId}`)
    }

    /**
     * One PATCH moves the entity. The server checks the destination and re-derives the child's
     * ACLs from it (GH #1958); the client used to compute them here and call `share` afterwards.
     */
    const moved = await dispatch(
      entitiesApi.endpoints.updateEntity.initiate({
        id: child.id,
        partialEntity: { parentId: parentId ?? 'ROOT' },
        eTag: childETag,
      })
    ).unwrap()

    /**
     * The RTK lists refetch on the mutation's tags, but the graph cache does not: a cached copy of the child kept
     * its old parentId, and `useEntityChildren` merges the cached children of a parent into its list, so the item
     * stayed in the source folder until a reload. Re-index it under the new parent. GH #2074
     */
    useGraphCacheStore.getState().upsertCache(moved)

    return moved
  }
)
